import Analytics from '@/utils/analytics';
import ExclamationPoint from '@assets/icons/exclamation-point.svg';
import moment from 'moment';
import React, { useMemo } from 'react';
import { Bubble, BubbleProps } from 'react-native-gifted-chat';
import { ChatMessageCreditRequest } from './chat-message-credit-request';
import { ChatMessageOrderInvoice } from './chat-message-order-invoice';
import { ChatMessageOrderStatusUpdate } from './chat-message-order-status-update';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_USERNAME_DISTANCE_IN_PX } from './constants';
import { Message, OnCreditRequestPressType, OnOrderPressType } from './types';
import { Platform, Text, TouchableOpacity } from 'react-native';
import { TYPOGRAPHY } from '@/styles';

const RETRY_HIT_SLOP = {
  bottom: 8,
  left: 8,
  right: 8,
  top: 8,
};

type Props = BubbleProps<Message> & {
  onCreditRequestPress: OnCreditRequestPressType;
  onOrderPress: OnOrderPressType;
  onRetrySend: (message: Message) => Promise<void>;
};

export const ChatBubble: React.FC<Props> = ({
  onCreditRequestPress,
  onOrderPress,
  onRetrySend,
  ...baseProps
}) => {
  const [styles, theme] = useStyles();

  const currentMessage = baseProps.currentMessage as Message;
  const previousMessage = baseProps.previousMessage as Message;

  const isOppositeMessage = useMemo(
    () =>
      !previousMessage ||
      previousMessage.user?._id !== currentMessage.user?._id,
    [currentMessage, previousMessage],
  );

  const isUserMessage = useMemo(
    () => currentMessage.user?._id === baseProps.user?._id,
    [baseProps, currentMessage],
  );

  const isFirstMessageOfGroup = useMemo(() => {
    return (
      isOppositeMessage ||
      !moment(currentMessage.createdAt).isSame(
        moment(previousMessage.createdAt),
        'day',
      )
    );
  }, [currentMessage, isOppositeMessage, previousMessage]);

  const isLastMessageOfGroup = useMemo(() => {
    return (
      !baseProps.nextMessage ||
      !moment(currentMessage.createdAt).isSame(
        moment(baseProps.nextMessage.createdAt),
        'day',
      )
    );
  }, [baseProps, currentMessage]);

  const messageBubbleProps = useMemo(() => {
    const usernamePadding =
      isFirstMessageOfGroup && !isUserMessage ? { marginTop: 16 } : {};
    const orderStatusUpdateWrapperStyle = {
      left: [styles.wrapper, styles.orderStatusUpdateWrapper, usernamePadding],
      right: [styles.wrapper, styles.orderStatusUpdateWrapper, usernamePadding],
    };

    if (currentMessage.orderStatusUpdate) {
      return {
        renderCustomView: (props: BubbleProps<Message>) => (
          <ChatMessageOrderStatusUpdate
            {...props}
            onOrderPress={onOrderPress}
          />
        ),
        renderMessageImage: () => null,
        renderMessageText: () => null,
        wrapperStyle: orderStatusUpdateWrapperStyle,
      };
    }

    if (currentMessage.orderInvoice) {
      return {
        renderCustomView: (props: BubbleProps<Message>) => (
          <ChatMessageOrderInvoice {...props} onOrderPress={onOrderPress} />
        ),
        renderMessageImage: () => null,
        renderMessageText: () => null,
        wrapperStyle: orderStatusUpdateWrapperStyle,
      };
    }

    if (currentMessage.creditRequestBatch) {
      return {
        renderCustomView: (props: BubbleProps<Message>) => (
          <ChatMessageCreditRequest
            {...props}
            onCreditRequestPress={onCreditRequestPress}
          />
        ),
        renderMessageImage: () => null,
        renderMessageText: () => null,
        wrapperStyle: orderStatusUpdateWrapperStyle,
      };
    }

    if (currentMessage.image) {
      return {
        containerStyle: {
          left: [styles.container, styles.imageContainer],
          right: [styles.container, styles.imageContainer],
        },
        wrapperStyle: {
          left: [styles.wrapper, styles.imageWrapperLeft, usernamePadding],
          right: [styles.wrapper, styles.imageWrapperRight, usernamePadding],
        },
      };
    }

    return {
      containerStyle: {
        left: styles.container,
        right: styles.container,
      },
      wrapperStyle: {
        left: [
          styles.wrapper,
          styles.wrapperLeft,
          isLastMessageOfGroup ? { borderBottomLeftRadius: 4 } : {},
          usernamePadding,
        ],
        right: [
          styles.wrapper,
          styles.wrapperRight,
          isLastMessageOfGroup ? { borderBottomRightRadius: 4 } : {},
          usernamePadding,
        ],
      },
    };
  }, [
    currentMessage,
    isFirstMessageOfGroup,
    isUserMessage,
    isLastMessageOfGroup,
    onCreditRequestPress,
    onOrderPress,
    styles,
  ]);

  const bubbleProps: BubbleProps<Message> = useMemo(() => {
    return {
      ...baseProps,
      ...messageBubbleProps,
      bottomContainerStyle: {
        left: styles.bottomContainer,
        right: styles.bottomContainer,
      },
    };
  }, [baseProps, messageBubbleProps, styles]);

  return (
    <>
      {currentMessage.error ? (
        <TouchableOpacity
          hitSlop={RETRY_HIT_SLOP}
          onPress={async () => {
            Analytics.track('chat_message.retry_button.press');
            await onRetrySend(currentMessage);
          }}
          style={styles.retryButton}
          testID="retry-button"
        >
          <ExclamationPoint fill={theme.colors.white} />
        </TouchableOpacity>
      ) : null}
      {isFirstMessageOfGroup && !isUserMessage ? (
        <Text style={styles.userName}>{currentMessage.user.name || '--'}</Text>
      ) : null}
      <Bubble {...bubbleProps} />
    </>
  );
};

const useStyles = createStyles((theme) => ({
  bottomContainer: {
    // Replaced display: 'none' with opacity: 0 to prevent Yoga layout crashes in RN 0.79+
    // See: https://github.com/facebook/react-native/issues/52349
    // Scoped to Android only as this is an Android-specific crash
    ...Platform.select({
      android: {
        height: 0,
        opacity: 0,
        pointerEvents: 'none' as const,
        width: 0,
      },
      default: {
        display: 'none' as const,
      },
    }),
  },
  container: {
    flex: -1,
  },
  imageContainer: {
    width: '60%',
  },
  imageWrapperLeft: {
    backgroundColor: theme.sk.color.bg.chat.received,
    width: '100%',
  },
  imageWrapperRight: {
    backgroundColor: theme.sk.color.bg.chat.sent,
    width: '100%',
  },
  orderStatusUpdateWrapper: {
    backgroundColor: theme.colors.transparent,
  },
  retryButton: {
    alignItems: 'center',
    alignSelf: 'center',
    backgroundColor: theme.colors.negative,
    borderRadius: 8,
    height: 16,
    justifyContent: 'center',
    marginRight: 8,
    width: 16,
  },
  userName: {
    ...TYPOGRAPHY.smallLabel,
    fontFamily: theme.fonts.primary,
    left: DEFAULT_USERNAME_DISTANCE_IN_PX,
    position: 'absolute',
    top: 0,
    zIndex: 1,
  },
  wrapper: {
    // gifted-chat has default border radius, we need to directly override it
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    marginLeft: 0,
    marginRight: 0,
    maxWidth: 564,
    overflow: 'hidden',
  },
  wrapperLeft: {
    backgroundColor: theme.sk.color.bg.chat.received,
  },
  wrapperRight: {
    backgroundColor: theme.sk.color.bg.chat.sent,
  },
}));
